import { DOCS_URL } from '@constants/constant';
import { FiltersProp, FilterType } from '@constants/data-table';
import { PermissionAction, PermissionAsset } from '@redux/types/types';

export const ROLES_PAGE_TITLE = 'Roles';
export const CREATE_ROLE = 'Create Role';
export const ROLES_PAGE_DESCRIPTION =
  'Manage roles and permissions for members in your organization to be applied on a workspace level.';
export const CREATE_ROLE_TEXT = 'Create Role';
export const CREATE_CUSTOM_ROLE_TEXT = 'Create Custom Role';
export const CUSTOM_TEXT = 'Custom';
export const CREATE_ROLE_DESCRIPTION =
  'Define a new role with custom permissions. Roles apply only at the workspace level';
export const ACCESS_TEXT = 'Access:';
export const CHOOSE_PERMISSIONS_TEXT = 'Choose Permissions';
export const SEARCH_PERMISSIONS_TEXT = 'Search for permissions';
export const NO_PERMISSIONS_FOUND_TEXT = 'No permissions found';
export const EXPAND_ALL_TEXT = 'Expand all';
export const COLLAPSE_ALL_TEXT = 'Collapse all';
export const PERMISSION_WILDCARD = '*';
export const LEARN_MORE_TEXT = 'Learn more';
export const LEARN_MORE_URL = `${DOCS_URL}docs/manage-users`;
export const LEARN_MORE_WITH_BUTTON = ` (%learnMoreButton%)`;
export const EDIT_ROLE_TEXT = 'Edit Role';
export const EDIT_ROLE_OK_TEXT = 'Save Changes';
export const ROLES_DELETE_ROLE_TITLE = 'Delete custom role';
export const ROLES_DELETE_ROLE_MESSAGE =
  'Deleting the %roleName% role is permanent. Any members or teams assigned to this role will lose the access it provides. Delete the role?';

export const ROLES_PAGE_TABS = {
  presetRoles: {
    key: 'presetRoles',
    label: 'Preset Roles',
  },
  customRoles: {
    key: 'customRoles',
    label: 'Custom Roles',
  },
};

export const PRESET_LABEL = 'Preset';
export const CUSTOM_LABEL = 'Custom';
export const ALL_ROLES_LABEL = 'All roles';
export const SORT_LABEL = 'Sort';
export const ROLES_EMPTY_LABEL =
  'No custom roles yet. Custom roles help you control exactly who can do what. Create one to get started.';
export const ROLES_SEARCH_EMPTY_LABEL = 'No roles found';

export const PRESET_ROLE_NAMES = {
  admin: 'Admin',
  editor: 'Editor',
  external: 'External',
  search_user: 'Search User',
};

export const ROLES_PRESET_TAB = {
  title: "Predefined roles with fixed permissions. You can't change or customize them.",
  tooltip: 'View Permissions',
  labels: {
    role: 'Role',
    description: 'Description',
  },
};

export const PERMISSION_VIEWER_MODAL = {
  DESCRIPTIONS: {
    PRESET:
      "This role has a fixed set of permissions you can't edit. All permissions apply at the workspace level.",
    CUSTOM: 'Roles apply only at workspace level.',
  },
  ACTIONS: {
    [PermissionAction.READ]: {
      LABEL: 'Read-only',
      DETAIL: 'Permission to view or retrieve data without making changes.',
    },
    [PermissionAction.WRITE]: {
      LABEL: 'Read & write',
      DETAIL: 'Permission to create, change, share, or delete resources.',
    },
  },
  ASSETS: {
    [PermissionAsset.FILES]: 'Files',
    [PermissionAsset.INDEXES]: 'Indexes',
    [PermissionAsset.PIPELINES]: 'Pipelines',
    [PermissionAsset.PROMPTS]: 'Prompts',
    [PermissionAsset.SHARED_PROTOTYPES]: 'Shared Prototypes',
    [PermissionAsset.JOBS]: 'Jobs',
    [PermissionAsset.SEARCH_HISTORY]: 'Search History',
    [PermissionAsset.SECRETS]: 'Secrets',
    [PermissionAsset.FEEDBACK]: 'Feedback',
    [PermissionAsset.API_KEYS]: 'API Keys',
    [PermissionAsset.ALL]: 'All',
  } as Record<PermissionAsset, string>,
  ROLE_NAME: 'Role Name',
  PERMISSIONS: 'Permissions',
} as const;

export const ROLES_LIST = {
  ROLE_LABEL: 'Role',
  DESCRIPTION_LABEL: 'Description',
  TOOLTIPS: {
    EDIT: 'Edit Role',
    DELETE: 'Delete Role',
    VIEW: 'View Permissions',
  },
};

export const ROLES_CUSTOM_TAB = {
  TITLE: 'Define roles for your organization and choose their permissions.',
};

export const ROLES_SEARCH_PLACEHOLDER = 'Search';

export const ROLES_SORTING_LABEL = 'Sort by';
export const ROLES_SORTING_VALUES = {
  DEFAULT: 'default',
  NAME_ASC: 'name_asc',
  NAME_DESC: 'name_desc',
};
export const ROLES_SORTING_OPTIONS = [
  { key: ROLES_SORTING_VALUES.DEFAULT, label: 'Default' },
  { key: ROLES_SORTING_VALUES.NAME_ASC, label: 'Name (A to Z)' },
  { key: ROLES_SORTING_VALUES.NAME_DESC, label: 'Name (Z to A)' },
];

export const ROLES_FILTER_OPTIONS: FiltersProp = [
  {
    key: 'role',
    style: { minWidth: '67px' },
    title: 'All roles',
    type: FilterType.SELECT,
    options: [{ key: 'all', label: 'All roles' }],
  },
];

export enum RoleType {
  COPY_PRESET_ROLE = 'copyPresetRole',
  START_FROM_SCRATCH = 'startFromScratch',
}

export const CREATE_ROLE_FORM_ITEMS = {
  roleName: {
    label: 'Role Name',
    name: 'roleName',
    placeholder: 'Give your role a name, for example: Prompt Engineer.',
  },
  roleDescription: {
    label: 'Description',
    name: 'roleDescription',
    placeholder: `Explain the role's purpose and permissions to help identify it later.`,
  },
  createRoleType: {
    label: 'Choose how to begin',
    name: 'roleType',
    options: [
      {
        label: 'Copy a preset role',
        value: RoleType.COPY_PRESET_ROLE,
      },
      {
        label: 'Start from scratch',
        value: RoleType.START_FROM_SCRATCH,
      },
    ],
  },
  roleToCopy: {
    label: 'Role to copy',
    name: 'roleToCopy',
    placeholder: 'Choose a role to copy',
  },
};

export const PERMISSION_ACCESS_OPTIONS = {
  NO_ACCESS: 'none',
  READ: 'read',
  WRITE: 'write',
};

export const PERMISSION_ACTIONS_LIST = {
  none: {
    label: 'No access',
    description: 'No permissions granted; no access to the resource.',
    value: PERMISSION_ACCESS_OPTIONS.NO_ACCESS,
  },
  readOnly: {
    label: 'Read-only',
    description: 'Permission to view or retrieve data without making changes.',
    value: PERMISSION_ACCESS_OPTIONS.READ,
  },
  readWrite: {
    label: 'Read & write',
    description: 'Permission to create, change, share, or delete resources.',
    value: PERMISSION_ACCESS_OPTIONS.WRITE,
  },
} as const;

export enum RolesMessages {
  CREATE_ROLE_SUCCESS = 'Role created',
  UPDATE_ROLE_SUCCESS = 'Role updated',
  DELETE_ROLE_SUCCESS = 'Role deleted',
}
